import { useQuery } from '@tanstack/react-query';
import { getPolicySummary, getPolicyDetail } from '@api/trackingPolicy';
import { PolicySummaryResponse, PolicyDetailResponse } from './types';
import { useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { updateInterval } from '@src/store/common/actions';
import { intervalSelector } from '@src/store/common/selectors';
import { selector as summaryComparisonSelector } from '@store/summaryContainer/comparison';
import { DASHBOARD_TYPE } from '@utils/constants';

const trackingSummaryKey = ['trackingSummary'];
const trackingDetailKey = ['trackingDetail'];

export const usePolicySummary = (params: { start_day: any; end_day: any }) => {
  const response = useQuery<PolicySummaryResponse>(
    [trackingSummaryKey, params.start_day, params.end_day],
    () => {
      return getPolicySummary<PolicySummaryResponse>(params);
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      staleTime: 5 * 1000,
    },
  );
  return response;
};

export const usePolicyDetail = (params: { policy_id: number; start_day: any; end_day: any }) => {
  const response = useQuery(
    [trackingDetailKey, params.policy_id, params.start_day, params.end_day],
    () => {
      return getPolicyDetail<PolicyDetailResponse>(params);
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
      staleTime: 5 * 1000,
    },
  );

  return response;
};

export const usePolicyInterval = () => {
  const interval = useSelector(intervalSelector);
  const comparison = useSelector(summaryComparisonSelector);
  const dispatch = useDispatch();
  const summaryDatePickerRef = useRef(null);
  const comparisonDatePickerRef = useRef(null);

  // reuse intervalSelector, but default is last 6 hours, in case of policy tracking the
  // granularity is 1 day
  const normalize = (a: moment.Moment, b: moment.Moment): [moment.Moment, moment.Moment] => {
    const start = moment.min(a, b);
    const end = moment.max(a, b);

    if (end.diff(start, 'hours') <= 24) {
      return [moment().startOf('day'), moment()];
    }

    return [start, end];
  };

  const [normStart, normEnd] = normalize(moment(interval.from), moment(interval.to));
  const [localInterval, setLocalInterval] = useState<[moment.Moment, moment.Moment]>([
    normStart,
    normEnd,
  ]);

  const hasIntervalChanged = useCallback(
    (newInterval) => {
      try {
        return !(
          newInterval[0].isSame(moment(interval.from)) && newInterval[1].isSame(moment(interval.to))
        );
      } catch {
        return true;
      }
    },
    [interval],
  );

  const validateUpdateInterval = useCallback(
    (dates: [moment.Moment, moment.Moment], short: string) => {
      if (hasIntervalChanged(dates)) {
        setLocalInterval([moment(dates[0]), moment(dates[1])]);
      }
      dispatch(
        updateInterval(dates[0].format(), dates[1].format(), short, DASHBOARD_TYPE.POLICY_TRACKING),
      );
    },
    [dispatch, hasIntervalChanged],
  );

  return {
    interval,
    comparisonInterval: comparison.interval,
    localInterval,
    validateUpdateInterval,
    summaryDatePickerRef,
    comparisonDatePickerRef,
  };
};
